'use client';

import { useCallback, useEffect, useState } from 'react';
import { Zap, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';

/**
 * Floor-sweep control — re-introduces classic.takeover.fun's "sweep N tiles
 * from the floor" slider, adapted to the lobby's basket model.
 *
 * It is purely a *selection* driver: dragging the slider fills the
 * multi-select basket with the `n` cheapest occupied (buyout) seats via
 * `onSweep` (which the lobby wires to `basket.selectExactly`, replace
 * semantics so the count tracks the slider both up and down). The existing
 * `BasketBar` → bulk-buy drawer then confirms the purchase in one
 * `addBatch`, so no new transaction path is introduced here.
 *
 * Collapsed it's a single discoverable pill; expanded it shows the slider +
 * live count. `×` cancels (clears the basket + collapses).
 */
export function SweepControl({
  maxCount,
  open,
  onOpenChange,
  onSweep,
  onClear,
  gold = false,
  className,
}: {
  /** Number of occupied "floor" seats buyable on this board right now. */
  maxCount: number;
  /** Expanded? Controlled by `LobbyClient` so the pill collapses in
   *  lock-step with the selection (board change / Esc / manual deselect). */
  open: boolean;
  /** Request an open-state change (slider reveal / Cancel). */
  onOpenChange: (open: boolean) => void;
  /** Select the `n` cheapest occupied seats (replace semantics). */
  onSweep: (n: number) => void;
  /** Clear the basket (fired by Cancel). */
  onClear: () => void;
  /** Full-board "golden takeover" — the sweep covers the whole floor, so
   *  the pill goes gold to match the board + basket. */
  gold?: boolean;
  className?: string;
}) {
  const [value, setValue] = useState(1);

  const clampMax = Math.max(1, maxCount);

  // If the board shrinks (seats bought out from under us) below the current
  // slider value while open, re-clamp and re-sweep so the basket can't hold
  // a count the board can no longer satisfy.
  useEffect(() => {
    if (!open) return;
    if (value > clampMax) {
      setValue(clampMax);
      onSweep(clampMax);
    }
  }, [open, value, clampMax, onSweep]);

  const handleOpen = useCallback(() => {
    const start = Math.min(5, clampMax);
    setValue(start);
    onOpenChange(true);
    onSweep(start);
  }, [clampMax, onSweep, onOpenChange]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    onClear();
  }, [onClear, onOpenChange]);

  const handleChange = useCallback(
    (vals: number[]) => {
      const n = Math.max(1, Math.min(clampMax, vals[0] ?? 1));
      setValue(n);
      onSweep(n);
    },
    [clampMax, onSweep],
  );

  if (maxCount <= 0) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          'pointer-events-auto inline-flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1.5',
          'bg-black/70 backdrop-blur-md font-mono text-[11px] tracking-wide',
          'transition-colors',
          gold
            ? 'border-amber-400/50 text-amber-100 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.12)] hover:border-amber-300/70 hover:bg-amber-400/10 hover:text-amber-50'
            : 'border-cyan-400/40 text-cyan-100 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.08)] hover:border-cyan-300/70 hover:bg-cyan-400/10 hover:text-cyan-50',
          className,
        )}
      >
        <Zap className={cn('h-3.5 w-3.5', gold ? 'text-amber-300' : 'text-cyan-300')} />
        Sweep the floor
      </button>
    );
  }

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-center gap-2.5 rounded-[8px] border px-3 py-2',
        'bg-black/80 backdrop-blur-md',
        gold
          ? 'border-amber-400/55 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.14)]'
          : 'border-cyan-400/50 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.1)]',
        className,
      )}
    >
      <Zap className={cn('h-3.5 w-3.5 shrink-0', gold ? 'text-amber-300' : 'text-cyan-300')} />
      <span className={cn('shrink-0 font-mono text-[10px] uppercase tracking-[0.15em]', gold ? 'text-amber-100/80' : 'text-cyan-100/70')}>
        {gold ? 'Floor swept' : 'Sweep'}
      </span>
      <Slider
        value={[value]}
        onValueChange={handleChange}
        min={1}
        max={clampMax}
        step={1}
        className="w-32 sm:w-40"
        aria-label="Number of floor tiles to sweep"
      />
      <span className={cn('min-w-[5ch] shrink-0 text-right font-mono text-[11px] tabular-nums', gold ? 'text-amber-50' : 'text-cyan-50')}>
        {value}/{clampMax}
      </span>
      <button
        type="button"
        onClick={handleCancel}
        aria-label="Cancel sweep"
        className={cn(
          'grid h-5 w-5 shrink-0 place-items-center rounded-[3px] transition-colors hover:bg-white/10',
          gold ? 'text-amber-100/60 hover:text-amber-50' : 'text-cyan-100/60 hover:text-cyan-50',
        )}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
